import { ErrorHandler } from '@/helpers/errorHandler';
import SentryClient from '@/helpers/sentry';
import { MICROSERVICES_ENUM, graphqlMutation, graphqlQuery } from '@/helpers/apollo';
import RootState from '@/store/root_state';
import { ActionTree } from 'vuex';
import { AlumniProfileState } from './state';
import {
  getLoggedUser,
  getUserProfilesByDexIds,
  getUserProfilesByIds,
} from '@/store/queries/westerosQueries';
import { addUser, setUserInteraction, setUserSettings } from '@/store/mutations/westerosMutations';
import { UserInteractionModel } from '@/models/alumni-profile/user-interaction.model';

const SET_ALUMNI_PROFILE = 'SET_ALUMNI_PROFILE';
const SET_ALUMNI_PROFILE_TOKEN = 'SET_ALUMNI_PROFILE_TOKEN';
const SET_PROFILES = 'SET_PROFILES';
const SET_INTERACTION = 'SET_INTERACTION';
const SET_USER_SETTINGS = 'SET_USER_SETTINGS';

export const getUserProfile = async (user: any) => {
  try {
    const queryResponse = await graphqlQuery(MICROSERVICES_ENUM.PROFILE, getLoggedUser, {
      descomplicaId: Number(user.descomplicaId),
    });

    if (queryResponse && queryResponse.data && queryResponse.data.getLoggedUser) {
      return queryResponse.data.getLoggedUser;
    }

    const nameArray = user.name ? user.name.split(' ') : [];
    const firstName = nameArray.length > 0 ? nameArray.shift() : null;
    const lastName = nameArray.length > 1 ? nameArray.pop() : null;
    const displayName = user.name;
    const email = user.email || '';

    const mutationResponse = await graphqlMutation(MICROSERVICES_ENUM.PROFILE, addUser, {
      descomplicaId: Number(user.descomplicaId),
      firstName,
      lastName,
      displayName,
      email,
    });

    if (mutationResponse && mutationResponse.data && mutationResponse.data.addUser) {
      return mutationResponse.data.addUser;
    }
  } catch (e) {
    SentryClient.captureError(e);
  }
  return null;
};

const actions: ActionTree<AlumniProfileState, RootState> = {
  async GET_ALUMNI_PROFILE({ commit }, payload): Promise<void> {
    const profileUser = await getUserProfile(payload);

    if (!profileUser) {
      return;
    }

    commit(SET_ALUMNI_PROFILE, profileUser);
    commit(SET_ALUMNI_PROFILE_TOKEN, profileUser.accessToken);
  },

  async GET_PROFILES_BY_DEX_IDS({ commit }, dexIds: number[]): Promise<void> {
    try {
      const { data } = await graphqlQuery(MICROSERVICES_ENUM.PROFILE, getUserProfilesByDexIds, {
        dexIds,
      });

      if (!data?.getUserProfilesByDexIds?.length) return;

      const profiles = data.getUserProfilesByDexIds;
      commit(SET_PROFILES, profiles);
    } catch (e) {
      SentryClient.captureError(e);
    }
  },

  async GET_PROFILES_BY_IDS({ commit }, profileIds: number[]): Promise<void> {
    try {
      const { data } = await graphqlQuery(MICROSERVICES_ENUM.PROFILE, getUserProfilesByIds, {
        profileIds,
      });

      if (!data?.getUserProfilesByIds?.length) return;

      const profiles = data.getUserProfilesByIds;
      commit(SET_PROFILES, profiles);
    } catch (e) {
      SentryClient.captureError(e);
    }
  },

  async SET_USER_INTERACTION({ commit }, userInteractions: UserInteractionModel): Promise<void> {
    try {
      const { data } = await graphqlMutation(MICROSERVICES_ENUM.PROFILE, setUserInteraction, {
        ...userInteractions,
      });

      if (!data?.setUserInteraction) {
        ErrorHandler.showErrorMessage({
          error: new Error('No data returned from setUserInteraction mutation'),
          defaultMessage: true,
        });
        return;
      }

      const userInteraction = data.setUserInteraction;
      commit(SET_INTERACTION, userInteraction);
    } catch (e) {
      SentryClient.captureError(e);
    }
  },
  async EDIT_USER_SETTINGS({ commit }, shouldAutoPauseVideo: boolean): Promise<void> {
    try {
      const { data } = await graphqlQuery(MICROSERVICES_ENUM.PROFILE, setUserSettings, {
        shouldAutoPauseVideo,
      });

      if (!data?.setUserSettings) return;

      const userSettings = data.setUserSettings;
      commit(SET_USER_SETTINGS, userSettings);
    } catch (e) {
      SentryClient.captureError(e);
    }
  },
};

export default actions;
